/**
 * Tool Feature Toggles
 *
 * Controls which CefSharp API tool categories are enabled at build time.
 *
 * Set VITE_ENABLED_TOOLS in your .env file as a comma-separated list:
 *   VITE_ENABLED_TOOLS=task,model,feature,selection,typeCatalog,automation,conversion,alignmentDesign
 *
 * Omit the variable or set it to "*" to enable all tools (default).
 * Set it to "" (empty) to disable all CefSharp tools.
 */

export type ToolCategory =
  | "task"
  | "model"
  | "feature"
  | "selection"
  | "typeCatalog"
  | "automation"
  | "conversion"
  | "spatialData"
  | "alignmentDesign"
  | "roadDesign"
  | "railwayDesign"
  | "waterSewerDesign"
  | "roadMarking";

const ALL_CATEGORIES: ToolCategory[] = [
  "task",
  "model",
  "feature",
  "selection",
  "typeCatalog",
  "automation",
  "conversion",
  "spatialData",
  "alignmentDesign",
  "roadDesign",
  "railwayDesign",
  "waterSewerDesign",
  "roadMarking",
];

export const CATEGORY_TO_NAMESPACE: Record<ToolCategory, string> = {
  task: "taskApi",
  model: "modelApi",
  feature: "featureApi",
  selection: "selectionApi",
  typeCatalog: "typeCatalogApi",
  automation: "automationApi",
  conversion: "conversionApi",
  spatialData: "spatialDataApi",
  alignmentDesign: "alignmentDesignApi",
  roadDesign: "roadDesignApi",
  railwayDesign: "railwayDesignApi",
  waterSewerDesign: "waterSewerDesignApi",
  roadMarking: "roadMarkingApi",
};

function parseEnabledTools(): Set<ToolCategory> {
  const raw = import.meta.env.VITE_ENABLED_TOOLS as string | undefined;

  if (raw === undefined || raw === "*") {
    return new Set(ALL_CATEGORIES);
  }

  if (raw.trim() === "") {
    return new Set();
  }

  const enabled = new Set<ToolCategory>();
  for (const token of raw.split(",")) {
    const trimmed = token.trim() as ToolCategory;
    if (ALL_CATEGORIES.includes(trimmed)) {
      enabled.add(trimmed);
    } else {
      console.warn(
        `[ToolToggles] Unknown tool category "${trimmed}" in VITE_ENABLED_TOOLS. ` +
        `Valid: ${ALL_CATEGORIES.join(", ")}`,
      );
    }
  }
  return enabled;
}

function parsePreviewTools(): Set<ToolCategory> {
  const raw = import.meta.env.VITE_ENABLED_PREVIEW_TOOLS as string | undefined;

  if (!raw || raw.trim() === "") {
    return new Set();
  }

  const enabled = new Set<ToolCategory>();
  for (const token of raw.split(",")) {
    const trimmed = token.trim() as ToolCategory;
    if (ALL_CATEGORIES.includes(trimmed)) {
      enabled.add(trimmed);
    } else {
      console.warn(
        `[ToolToggles] Unknown tool category "${trimmed}" in VITE_ENABLED_PREVIEW_TOOLS. ` +
        `Valid: ${ALL_CATEGORIES.join(", ")}`,
      );
    }
  }
  return enabled;
}

export const enabledTools: Set<ToolCategory> = parseEnabledTools();

export const previewTools: Set<ToolCategory> = parsePreviewTools();

export const isToolEnabled = (category: ToolCategory): boolean =>
  enabledTools.has(category);

export const isPreviewTool = (category: ToolCategory): boolean =>
  previewTools.has(category);

export const enabledNamespaces: Set<string> = new Set(
  [...enabledTools].map((c) => CATEGORY_TO_NAMESPACE[c]),
);
